import Tracker from "../../../../../../prop-trackers/tracker.js";
import SinglePropTracker from "../../../../../../prop-trackers/single-prop-tracker.js";
import DictTracker from "../../../../../../prop-trackers/dict-tracker.js";
import { ForEach, ValueTracker as ForEachValueTracker } from "../../../../../../prop-tracker-utils/for-each.js";

function getFirstKey(obj) {
    for (const key in obj) {
        return key;
    }
}

class ObjectRootsGenerator extends Tracker {
    constructor(childTracker, rootTracker, evtCtx) {
        super();

        this._childData = {};
        this._rootData = {};
        this._objectRoots = {};

        this._childTracker = childTracker;
        this._rootTracker = rootTracker;

        this._forEachChildTracker = new DictTracker();
        this._forEachTracker = new ForEach(this._forEachChildTracker, (key) => new ParentTracker(evtCtx, key));
        this._updateObjects = {};

        this._outputTracker = new DictTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._childTracker.listenNeedsUpdate(needsUpdate);
        this._rootTracker.listenNeedsUpdate(needsUpdate);
        this._forEachTracker.listenNeedsUpdate(needsUpdate);
    }

    protected_startUpdate() {
        this._childTracker.updateIfNeeded(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                if (newExists) {
                    this._childData[key] = newValue;
                    this._forEachChildTracker.setValue(key, newValue);
                } else {
                    delete this._childData[key];
                    this._forEachChildTracker.deleteValue(key);
                }
            }
        });

        this._rootTracker.updateIfNeeded(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                this._updateObjects[key] = true;

                if (newExists) {
                    this._rootData[key] = newValue;
                } else {
                    delete this._rootData[key];
                }
            }
        });

        this._forEachTracker.updateIfNeeded(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: childKey, prop: { newValue: parentKey } }) => {
                    this._updateObjects[childKey] = true;
                });
            }
        });

        while (true) {
            const key = getFirstKey(this._updateObjects);

            if (key === undefined) {
                break;
            }

            let rootKey = null;

            if (key in this._rootData) {
                rootKey = key;
            } else if (key in this._childData) {
                const parentKey = this._childData[key].parent;

                if (parentKey && parentKey in this._objectRoots) {
                    rootKey = this._objectRoots[parentKey];
                }
            }

            if (rootKey) {
                this._objectRoots[key] = rootKey;
                this._outputTracker.setValue(key, rootKey);
            } else {
                delete this._objectRoots[key];
                this._outputTracker.deleteValue(key);
            }

            if (key in this._childData) {
                const childKeys = this._childData[key].children;

                for (const childKey in childKeys) {
                    this._updateObjects[childKey] = true;
                }
            }

            delete this._updateObjects[key];
        }

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        return this._outputTracker.finishUpdate();
    }
}

class ParentTracker extends ForEachValueTracker {
    constructor(evtCtx, key) {
        super();

        this._evtCtx = evtCtx;
        this._parentTracker = new SinglePropTracker(key);

        const needsUpdate = () => this.protected_needsUpdate();
        this._parentTracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(data) {
        this._parentTracker.track(data, data ? this._evtCtx.getEvents(data) : null, "parent");
    }

    deleteValue() {
        this._parentTracker.track();
    }

    protected_startUpdate() {
        return this._parentTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._parentTracker.finishUpdate();
    }
}

export default ObjectRootsGenerator;
